import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useMounted } from '@/lib/use-mounted';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Lock, CheckCircle2, Loader2, ShieldCheck, ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react';
import { useSubmitApplication } from '@workspace/api-client-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { SEO } from '@/components/SEO';
import { SITE_URL, SITE_NAME } from '@/lib/seo';
import { trackEvent } from '@/lib/analytics';

const applySchema = z.object({
  firstName: z.string().min(2, "Required"),
  lastName: z.string().min(2, "Required"),
  email: z.string().email("Valid email required"),
  phone: z.string().min(10, "Valid phone required"),
  dob: z.string().min(1, "Required"),
  ssn: z.string().min(9, "Required"),
  businessName: z.string().min(2, "Required"),
  businessType: z.string().min(1, "Required"),
  yearsInBusiness: z.string().min(1, "Required"),
  annualRevenue: z.string().min(1, "Required"),
  taxId: z.string().min(9, "Required"),
  equipmentType: z.string().min(1, "Required"),
  requestedAmount: z.string().min(1, "Required"),
  downPayment: z.string().optional(),
  preferredTerm: z.string().min(1, "Required"),
  creditScore: z.string().min(1, "Required"),
  year: z.string().min(4, "Required"),
  make: z.string().min(2, "Required"),
  model: z.string().min(2, "Required"),
  mileage: z.string().min(1, "Required"),
  askingPrice: z.string().min(1, "Required"),
});

type ApplyValues = z.infer<typeof applySchema>;
type FieldName = keyof ApplyValues;

const inputClass = "bg-white border-gray-300 text-[#1a2f36] h-12 focus-visible:ring-[#1a2f7e]";
const labelClass = "text-[#1a2f36]/80 font-semibold";
const selectContentClass = "bg-white border-gray-200 text-[#1a2f36]";

const steps: { title: string; shortTitle: string; fields: FieldName[] }[] = [
  {
    title: "Personal Information",
    shortTitle: "Personal",
    fields: ["firstName", "lastName", "email", "phone", "dob", "ssn"],
  },
  {
    title: "Business Information",
    shortTitle: "Business",
    fields: ["businessName", "businessType", "yearsInBusiness", "annualRevenue", "taxId"],
  },
  {
    title: "Financing Request",
    shortTitle: "Financing",
    fields: ["equipmentType", "creditScore", "requestedAmount", "downPayment", "preferredTerm"],
  },
  {
    title: "Equipment Details",
    shortTitle: "Equipment",
    fields: ["year", "make", "model", "mileage", "askingPrice"],
  },
];

export default function Apply() {
  const [isSuccess, setIsSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const startedRef = React.useRef(false);

  function handleFormStarted() {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent('apply_form_started');
  }

  const submitMutation = useSubmitApplication({
    mutation: {
      onSuccess: () => {
        setIsSuccess(true);
        setSubmitError(null);
        trackEvent('apply_form_submitted');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      onError: () => {
        setSubmitError(
          "We couldn't send your application right now. Please try again, or call us at 1-866-441-0513."
        );
      },
    },
  });
  const isSubmitting = submitMutation.isPending;

  const searchParams = useMemo(() =>
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
  , []);
  const creditTierMap: Record<string, string> = { A: 'excellent', B: 'good', C: 'fair', D: 'poor' };

  const form = useForm<ApplyValues>({
    resolver: zodResolver(applySchema),
    mode: 'onTouched',
    defaultValues: {
      firstName: "", lastName: "", email: "", phone: "", dob: "", ssn: "",
      businessName: "", businessType: "", yearsInBusiness: "", annualRevenue: "", taxId: "",
      equipmentType: "",
      requestedAmount: searchParams.get('requestedAmount') || "",
      downPayment: searchParams.get('downPayment') || "",
      preferredTerm: searchParams.get('preferredTerm') || "",
      creditScore: creditTierMap[searchParams.get('creditScore') || ''] || searchParams.get('creditScore') || "",
      year: "", make: "", model: "", mileage: "", askingPrice: ""
    }
  });

  async function onSubmit(values: ApplyValues) {
    setSubmitError(null);
    await submitMutation.mutateAsync({ data: values }).catch(() => {
      /* handled by onError */
    });
  }

  async function handleNext() {
    const valid = await form.trigger(steps[currentStep].fields, { shouldFocus: true });
    if (!valid) return;
    trackEvent('apply_form_step_completed', {
      step_number: currentStep + 1,
      step_name: steps[currentStep].shortTitle,
    });
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleBack() {
    setCurrentStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const mounted = useMounted();
  const isLastStep = currentStep === steps.length - 1;
  const progressPct = ((currentStep + 1) / steps.length) * 100;

  return (
    <Layout>
      <SEO
        title="Apply for Financing — 2-Minute Application"
        description="Apply online for commercial truck or equipment financing in 2 minutes. Credit decisions within 2 business hours. Funding wired in 24 hours. All credit profiles welcome."
        path="/apply"
        jsonLd={{
          "@type": "WebPage",
          url: `${SITE_URL}/apply`,
          name: `Apply for Financing | ${SITE_NAME}`,
          isPartOf: { "@id": `${SITE_URL}/#organization` },
          potentialAction: {
            "@type": "ApplyAction",
            target: `${SITE_URL}/apply`,
            name: "Submit a financing application",
          },
        }}
      />
      {/* HERO — dark navy accent header */}
      <section className="pt-28 sm:pt-32 pb-8 sm:pb-10 bg-[#1a2f7e]">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.h1
            key={mounted ? "hero-on" : "hero-off"} initial={mounted ? { opacity: 0, y: 20 } : false} animate={{ opacity: 1, y: 0 }}
            className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 sm:mb-6 uppercase tracking-tight"
          >
            Application for <span className="text-[#cc2020]">Commercial Fleet Financing</span>
          </motion.h1>

          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 sm:gap-6 text-xs sm:text-sm font-bold uppercase tracking-wider text-white/80 mt-6 sm:mt-8">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" /> Decisions in 2 Hours</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" /> Funding in 24 Hours</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" /> All Credit Accepted</span>
          </div>
        </div>
      </section>

      {/* FORM — white background */}
      <section className="py-10 sm:py-16 bg-white min-h-[60vh]">
        <div className="container mx-auto px-4 max-w-4xl">
          {isSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="bg-white border border-gray-200 rounded-sm p-6 sm:p-12 text-center shadow-sm"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <CheckCircle2 className="w-10 h-10 sm:w-12 sm:h-12 text-green-600" />
              </div>
              <h2 className="text-xl sm:text-3xl font-bold text-[#1a2f36] mb-3 sm:mb-4 uppercase">Application Submitted Successfully</h2>
              <p className="text-base sm:text-xl text-slate-600 mb-6 sm:mb-8">
                Your application has been securely transmitted to our underwriting team. A finance specialist will review your file and contact you within 2 business hours with a decision.
              </p>
              <Button
                onClick={() => window.location.href = '/'}
                className="bg-[#1a2f7e] hover:bg-[#152568] text-white font-bold h-12 px-8 uppercase tracking-wide"
              >
                Return to Home
              </Button>
            </motion.div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-4 sm:p-6 md:p-10">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8 pb-4 sm:pb-6 border-b border-gray-200">
                <h2 className="text-xl sm:text-2xl font-bold text-[#1a2f36] uppercase tracking-tight">Secure Application</h2>
                <div className="inline-flex self-start items-center gap-2 text-green-700 text-xs sm:text-sm font-bold uppercase tracking-widest bg-green-50 border border-green-200 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                  <Lock className="w-4 h-4" /> 256-Bit SSL Secure
                </div>
              </div>

              {/* Progress indicator */}
              <div className="mb-8 sm:mb-10">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-[#1a2f7e]">
                    Step {currentStep + 1} of {steps.length}
                  </p>
                  <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-[#1a2f36]/70">
                    {steps[currentStep].title}
                  </p>
                </div>
                <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-[#1a2f7e]"
                    initial={false}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <ol className="hidden sm:grid grid-cols-4 gap-2 mt-4">
                  {steps.map((s, i) => {
                    const done = i < currentStep;
                    const active = i === currentStep;
                    return (
                      <li
                        key={s.shortTitle}
                        className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${
                          active ? 'text-[#1a2f7e]' : done ? 'text-green-700' : 'text-gray-400'
                        }`}
                      >
                        <span
                          className={`w-6 h-6 flex items-center justify-center rounded-sm text-[11px] shrink-0 ${
                            active
                              ? 'bg-[#1a2f7e] text-white'
                              : done
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-200 text-gray-500'
                          }`}
                        >
                          {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                        </span>
                        {s.shortTitle}
                      </li>
                    );
                  })}
                </ol>
              </div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} onFocusCapture={handleFormStarted} className="space-y-8 sm:space-y-10">

                  {/* Step 1: Personal Information */}
                  {currentStep === 0 && (
                    <motion.div
                      key="step-1"
                      initial={mounted ? { opacity: 0, x: 20 } : false} animate={{ opacity: 1, x: 0 }}
                      className="space-y-6"
                    >
                      <h3 className="text-lg sm:text-xl font-bold text-[#1a2f36] uppercase tracking-wide flex items-center gap-2">
                        <span className="bg-[#1a2f7e] text-white w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-sm text-sm shrink-0">1</span>
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <FormField control={form.control} name="firstName" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>First Name</FormLabel><FormControl><Input className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="lastName" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Last Name</FormLabel><FormControl><Input className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="email" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Email Address</FormLabel><FormControl><Input type="email" className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="phone" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Phone Number</FormLabel><FormControl><Input type="tel" className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="dob" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Date of Birth</FormLabel><FormControl><Input type="date" data-clarity-mask="true" className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="ssn" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Social Security Number</FormLabel><FormControl><Input type="password" placeholder="XXX-XX-XXXX" data-clarity-mask="true" className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2: Business Information */}
                  {currentStep === 1 && (
                    <motion.div
                      key="step-2"
                      initial={mounted ? { opacity: 0, x: 20 } : false} animate={{ opacity: 1, x: 0 }}
                      className="space-y-6"
                    >
                      <h3 className="text-lg sm:text-xl font-bold text-[#1a2f36] uppercase tracking-wide flex items-center gap-2">
                        <span className="bg-[#1a2f7e] text-white w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-sm text-sm shrink-0">2</span>
                        Business Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <FormField control={form.control} name="businessName" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Legal Business Name</FormLabel><FormControl><Input className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="businessType" render={({ field }) => (
                          <FormItem>
                            <FormLabel className={labelClass}>Business Type</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl><SelectTrigger className={inputClass}><SelectValue placeholder="Select type" /></SelectTrigger></FormControl>
                              <SelectContent className={selectContentClass}>
                                <SelectItem value="sole">Sole Proprietorship</SelectItem>
                                <SelectItem value="llc">LLC</SelectItem>
                                <SelectItem value="corp">Corporation</SelectItem>
                                <SelectItem value="partnership">Partnership</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-[#cc2020]" />
                          </FormItem>
                        )} />
                        <FormField control={form.control} name="yearsInBusiness" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Years in Business</FormLabel><FormControl><Input type="number" className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="annualRevenue" render={({ field }) => (
                          <FormItem>
                            <FormLabel className={labelClass}>Annual Revenue</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl><SelectTrigger className={inputClass}><SelectValue placeholder="Select range" /></SelectTrigger></FormControl>
                              <SelectContent className={selectContentClass}>
                                <SelectItem value="0-100k">Under $100k</SelectItem>
                                <SelectItem value="100k-500k">$100k – $500k</SelectItem>
                                <SelectItem value="500k-1m">$500k – $1M</SelectItem>
                                <SelectItem value="1m+">Over $1M</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-[#cc2020]" />
                          </FormItem>
                        )} />
                        <FormField control={form.control} name="taxId" render={({ field }) => (
                          <FormItem className="md:col-span-2"><FormLabel className={labelClass}>Federal Tax ID (EIN)</FormLabel><FormControl><Input data-clarity-mask="true" className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                      </div>
                    </motion.div>
                  )}

                  {/* Step 3: Financing Request */}
                  {currentStep === 2 && (
                    <motion.div
                      key="step-3"
                      initial={mounted ? { opacity: 0, x: 20 } : false} animate={{ opacity: 1, x: 0 }}
                      className="space-y-6"
                    >
                      <h3 className="text-lg sm:text-xl font-bold text-[#1a2f36] uppercase tracking-wide flex items-center gap-2">
                        <span className="bg-[#1a2f7e] text-white w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-sm text-sm shrink-0">3</span>
                        Financing Request
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <FormField control={form.control} name="equipmentType" render={({ field }) => (
                          <FormItem>
                            <FormLabel className={labelClass}>Equipment Type</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl><SelectTrigger className={inputClass}><SelectValue placeholder="Select equipment" /></SelectTrigger></FormControl>
                              <SelectContent className={selectContentClass}>
                                <SelectItem value="semi">Semi-Truck</SelectItem>
                                <SelectItem value="trailer">Trailer</SelectItem>
                                <SelectItem value="dump">Dump Truck</SelectItem>
                                <SelectItem value="box">Box Truck</SelectItem>
                                <SelectItem value="tow">Tow Truck</SelectItem>
                                <SelectItem value="construction">Construction Equipment</SelectItem>
                                <SelectItem value="vocational">Vocational Vehicle</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-[#cc2020]" />
                          </FormItem>
                        )} />
                        <FormField control={form.control} name="creditScore" render={({ field }) => (
                          <FormItem>
                            <FormLabel className={labelClass}>Estimated Credit Score</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl><SelectTrigger className={inputClass}><SelectValue placeholder="Select score" /></SelectTrigger></FormControl>
                              <SelectContent className={selectContentClass}>
                                <SelectItem value="excellent">Excellent (700+)</SelectItem>
                                <SelectItem value="good">Good (640–699)</SelectItem>
                                <SelectItem value="fair">Fair (550–639)</SelectItem>
                                <SelectItem value="poor">Challenged (&lt;549)</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-[#cc2020]" />
                          </FormItem>
                        )} />
                        <FormField control={form.control} name="requestedAmount" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Requested Amount ($)</FormLabel><FormControl><Input type="number" className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="downPayment" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Down Payment ($) — Optional</FormLabel><FormControl><Input type="number" className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="preferredTerm" render={({ field }) => (
                          <FormItem className="md:col-span-2">
                            <FormLabel className={labelClass}>Preferred Term</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl><SelectTrigger className={inputClass}><SelectValue placeholder="Select term" /></SelectTrigger></FormControl>
                              <SelectContent className={selectContentClass}>
                                <SelectItem value="12">12 Months</SelectItem>
                                <SelectItem value="24">24 Months</SelectItem>
                                <SelectItem value="36">36 Months</SelectItem>
                                <SelectItem value="48">48 Months</SelectItem>
                                <SelectItem value="60">60 Months</SelectItem>
                                <SelectItem value="72">72 Months</SelectItem>
                                <SelectItem value="84">84 Months</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-[#cc2020]" />
                          </FormItem>
                        )} />
                      </div>
                    </motion.div>
                  )}

                  {/* Step 4: Equipment Details */}
                  {currentStep === 3 && (
                    <motion.div
                      key="step-4"
                      initial={mounted ? { opacity: 0, x: 20 } : false} animate={{ opacity: 1, x: 0 }}
                      className="space-y-6"
                    >
                      <h3 className="text-lg sm:text-xl font-bold text-[#1a2f36] uppercase tracking-wide flex items-center gap-2">
                        <span className="bg-[#1a2f7e] text-white w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-sm text-sm shrink-0">4</span>
                        Equipment Details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        <FormField control={form.control} name="year" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Year</FormLabel><FormControl><Input type="number" className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="make" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Make</FormLabel><FormControl><Input className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="model" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Model</FormLabel><FormControl><Input className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="mileage" render={({ field }) => (
                          <FormItem><FormLabel className={labelClass}>Mileage / Hours</FormLabel><FormControl><Input type="number" className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                        <FormField control={form.control} name="askingPrice" render={({ field }) => (
                          <FormItem className="lg:col-span-2"><FormLabel className={labelClass}>Asking Price ($)</FormLabel><FormControl><Input type="number" className={inputClass} {...field} /></FormControl><FormMessage className="text-[#cc2020]" /></FormItem>
                        )} />
                      </div>
                    </motion.div>
                  )}

                  <div className="pt-6 border-t border-gray-200">
                    {isLastStep && (
                      <div className="flex items-start gap-3 mb-6 sm:mb-8 text-slate-500 text-xs sm:text-sm bg-slate-50 border border-gray-200 rounded-sm p-3 sm:p-4">
                        <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8 text-[#1a2f7e] flex-shrink-0 mt-0.5" />
                        <p>By clicking "Submit Application", you authorize Truck Max Equipment Finance to review your credit history and provide the information to lending partners for the purpose of obtaining commercial financing.</p>
                      </div>
                    )}

                    {submitError && isLastStep && (
                      <div
                        role="alert"
                        className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-sm text-[#a81a1a]"
                      >
                        <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                        <p className="text-sm font-medium">{submitError}</p>
                      </div>
                    )}

                    <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4">
                      {currentStep > 0 && (
                        <Button
                          type="button"
                          onClick={handleBack}
                          variant="outline"
                          className="w-full sm:w-auto h-14 sm:h-16 text-base sm:text-lg font-bold uppercase tracking-wider border-[#1a2f7e] text-[#1a2f7e] hover:bg-[#1a2f7e]/5 rounded-sm"
                          disabled={isSubmitting}
                        >
                          <ArrowLeft className="w-5 h-5 mr-2" /> Back
                        </Button>
                      )}

                      {isLastStep ? (
                        <Button
                          type="submit"
                          className="w-full flex-1 h-14 sm:h-16 text-base sm:text-lg font-bold uppercase tracking-wider bg-[#cc2020] hover:bg-[#a81a1a] text-white rounded-sm"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Loader2 className="w-6 h-6 animate-spin" /> : "Submit Secure Application"}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={handleNext}
                          className="w-full flex-1 h-14 sm:h-16 text-base sm:text-lg font-bold uppercase tracking-wider bg-[#1a2f7e] hover:bg-[#152568] text-white rounded-sm"
                        >
                          Continue <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                      )}
                    </div>
                  </div>
                </form>
              </Form>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
